import {
	Component,
	OnInit,
	OnDestroy,
	Input,
	Output,
	ViewChild,
	ElementRef,
	EventEmitter
} from '@angular/core';
import {
	trigger,
	state,
	style,
	transition,
	animate,
	query,
	stagger
} from '@angular/animations';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerService } from '../../services/customer.service';
import { PrintService } from '../../services/print.service'
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { InventoryService } from '../../services/inventory.service';

@Component({
	selector: 'app-top-customers',
	templateUrl: './top-customers.component.html',
	styleUrls: ['./top-customers.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({ transform: 'rotate3d(0, 1, 0, 180deg)' })),
			state('inactive', style({ transform: 'rotateY(0)' })),
			transition('active <=> inactive', animate('500ms ease-in-out'))
		]),
		trigger('grow', [
			transition(':enter', [
				style({ height: '0', width: '0', opacity: 0 }),
				animate('200ms ease-out', style({ height: '*', width: '*', opacity: 1 }))
			]),
			transition(':leave', [
				animate('100ms ease-in', style({ height: 0, width: 0, opacity: 0 }))
			])
		]),
		trigger('filterAnimation', [
			transition(':increment', [
				query(':enter', [
					style({ opacity: 0, width: 0 }),
					stagger(50, animate('300ms ease-out', style({ opacity: 1, width: '*' })))
				], { optional: true })
			]),
			transition(':decrement', [
				query(':leave', [
					stagger(50, animate('300ms ease-out', style({ opacity: 0, width: 0 })))
				])
			])
		])
	]
})
export class TopCustomersComponent implements OnInit, OnDestroy {
	@Input() flip = 'inactive';
	@Input() limit = 100;
	@Output() viewData = new EventEmitter < any > ();

	@ViewChild('customerDetailsRef') customerDetailsRef: ElementRef;

	title = 'Top ' + this.limit + ' Customers';
	viewing: any = false;
	data: any = false;
	topcustomers: any[] = [];
	customerdetails: any[] = [];
	categories: any[] = [];
	productlines: any[] = [];
	brands: any[] = [];
	loading = true;
	searchForm: any = new UntypedFormGroup({});
	fromdate: Date;
	todate: Date;
	private subs: Subscription[] = [];

	constructor(
		private customerService: CustomerService,
		private fb: UntypedFormBuilder,
		private modalService: NgbModal,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private inventoryService: InventoryService
	) {}

	ngOnInit(): void {
		this.title = 'Top ' + this.limit + ' Customers';
		const today = new Date();
		this.fromdate = new Date(today.getFullYear(), today.getMonth()-3, 1);
		this.todate = today;
		const lim = Math.min(500, Math.max(1, Math.floor(Number(this.limit)) || 100));
		this.limit = lim;
		this.searchForm = this.fb.group({
			fromDateCtrl: [this.fromdate],
			toDateCtrl: [this.todate],
			brand: [[]],
			category: [[]],
			prodline: [[]],
			limit: [lim, [Validators.min(1), Validators.max(500)]],
		});
		this.subs.push(this.inventoryService.getItemCategories().subscribe((c: any) => { this.categories = (c as any[]) || []; }));
		this.subs.push(this.inventoryService.getItemProductlines().subscribe((p: any) => { this.productlines = (p as any[]) || []; }));
		this.subs.push(this.inventoryService.getBrands().subscribe((b: any) => { this.brands = b || []; }));
		this.reloadCustomers();
	}

	ngOnDestroy(): void {
		this.subs.forEach((s) => { if (s) { s.unsubscribe(); } });
		this.subs = [];
	}

	branchAddress(row: any): string {
		if (!row) {
			return '';
		}
		const parts = [row.braddress1, row.braddress2, row.braddress3, row.braddress4, row.braddress5, row.braddress6]
			.map((p: string) => (p != null && String(p).trim() !== '' ? String(p).trim() : null))
			.filter(Boolean);
		return parts.join(', ');
	}

	billAddress(row: any): string {
		if (!row) {
			return '';
		}
		const parts = [row.bill_address1, row.bill_address2, row.bill_address3, row.bill_address4, row.bill_address5, row.bill_address6]
			.map((p: string) => (p != null && String(p).trim() !== '' ? String(p).trim() : null))
			.filter(Boolean);
		return parts.join(', ');
	}

	get reportLimit(): number {
		const v = Number(this.searchForm?.get('limit')?.value);
		if (!Number.isFinite(v)) {
			return 100;
		}
		return Math.min(500, Math.max(1, Math.floor(v)));
	}

	reloadCustomers() {
		this.loading = true;
		const v = this.searchForm.value;
		const data: any = {
			limit: this.reportLimit,
			datefrom: v.fromDateCtrl,
			dateto: v.toDateCtrl,
		};
		if (v.brand?.length) {
			data.brand = v.brand;
		}
		if (v.category?.length) {
			data.category = v.category;
		}
		if (v.prodline?.length) {
			data.prodline = v.prodline;
		}
		this.customerService.getTopCustomers(data).subscribe(
			(results: any[]) => {
				this.topcustomers = results || [];
				this.data = this.topcustomers;
				this.loading = false;
				this.viewing = this.data;
			},
			() => {
				this.loading = false;
			}
		);
	}

	getReportSubtitle() {
		const out = '';
		return out;
	}

	toggleFlip(data: any, title: string) {
		this.flip = data ? (this.flip === 'active' ? 'inactive' : 'active') : 'inactive';

		if (this.flip === 'active') {
			this.viewing = data;
			this.title = title;
			this.viewData.emit({ data, title: this.title });
		} else {
			this.viewData.emit(false);
		}
	}

	exportPdf() {
		const now = new Date();
		const html = this.generateTopCustomersHtml(this.viewing);
		const lim = this.reportLimit;
		const filename = `top${lim}_customers_${now.getMonth() + 1}${now.getDate()}${now.getFullYear()}`;

		const data = {
			content: this.globalSearchService.base64encode(html),
			filename,
			title: `Top ${lim} Customers - Created @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};

		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, filename);
		});
	}

	exportXls() {
		const now = new Date();
		const html = this.generateTopCustomersHtml(this.viewing);
		const lim = this.reportLimit;
		const filename = `top${lim}_customers_${now.getMonth() + 1}${now.getDate()}${now.getFullYear()}`;

		const data = {
			content: this.globalSearchService.base64encode(html),
			filename,
			title: `Top ${lim} Customers - Created @ ${now.toLocaleString()}`,
			subtitle: this.getReportSubtitle()
		};

		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, filename);
		});
	}


	generateTopCustomersHtml(viewing: any[]): string {
		if (!viewing?.length) {
			return '<p><strong>No records to display.</strong></p>';
		}

		let html = `
			<table border="1" cellpadding="6" cellspacing="0" style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px;">
				<thead style="background-color: #f2f2f2;">
					<tr>
						<th align="left">Customer #</th>
						<th align="left">Name</th>
						<th align="left">Contact</th>
						<th align="left">Phone</th>
						<th align="left">Email</th>
						<th align="left">Ship-to address</th>
						<th align="left">Bill-to address</th>
						<th align="left">Last Seen</th>
						<th align="right">Sales Total</th>
						<th align="right">Units</th>
					</tr>
				</thead>
				<tbody>
		`;

		for (const d of viewing) {
			const ship = this.branchAddress(d);
			const bill = this.billAddress(d);
			html += `
				<tr>
					<td>${d.debtorno}.${d.branchcode}</td>
					<td>${d.name || d.deliverto || ''}</td>
					<td>${d.contactname || ''}</td>
					<td>${d.phoneno || ''}</td>
					<td>${d.email || ''}</td>
					<td>${ship}</td>
					<td>${bill}</td>
					<td>${d.lastseen || ''}</td>
					<td align="right">${(d.salestotal ?? 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</td>
					<td align="right">${(d.qtytotal ?? 0).toLocaleString()}</td>
				</tr>
			`;
		}

		html += `
				</tbody>
			</table>
		`;

		return html;
	}


	viewCustomer(debtor: any) {
		const req = {
			customerno: debtor.debtorno,
			branchcode: debtor.branchcode,
			details: true
		};

		this.customerService.getCustomer(debtor.debtorno, debtor.branchcode).subscribe(results => {
			this.customerdetails = Array.isArray(results) ? results : [results];
			this.modalService
				.open(this.customerDetailsRef, {
					ariaLabelledBy: 'modal-title',
					size: 'lg'
				})
				.result.finally(() => {
					this.customerdetails = [];
				});
		});
	}
}